/**
 * Game Balance Service
 * Handles iGaming account balance updates for game wins/losses
 */

import { igamingService } from './igamingService';

export class GameBalanceService {
  /**
   * Place a bet - deducts from balance
   */
  static async placeBet(amount: number): Promise<number> {
    try {
      const response = await igamingService.placeGameBet(amount);
      console.log('Bet placed:', amount, 'New balance:', response.newBalance);
      return response.newBalance;
    } catch (error) {
      console.error('Failed to place bet:', error);
      throw error;
    }
  }

  /**
   * Credit winnings - adds to balance
   */
  static async creditWinnings(amount: number): Promise<number> {
    try {
      const response = await igamingService.creditWinnings(amount);
      console.log('Winnings credited:', amount, 'New balance:', response.newBalance);
      return response.newBalance;
    } catch (error) {
      console.error('Failed to credit winnings:', error);
      throw error;
    }
  }

  /**
   * Record loss - for tracking only (balance already deducted with bet)
   */
  static async recordLoss(amount: number): Promise<number> {
    try {
      const response = await igamingService.recordLoss(amount);
      console.log('Loss recorded:', amount, 'New balance:', response.newBalance);
      return response.newBalance;
    } catch (error) {
      console.error('Failed to record loss:', error);
      throw error;
    }
  }

  /**
   * Handle Blackjack game result
   */
  static async handleBlackjackResult(
    betAmount: number,
    outcome: 'win' | 'lose' | 'push' | 'blackjack',
    splitBetAmount?: number
  ): Promise<number> {
    try {
      if (outcome === 'push') {
        // Push: return bet to player (credit the bet back)
        return await this.creditWinnings(betAmount + (splitBetAmount || 0));
      } else if (outcome === 'blackjack') {
        // Blackjack pays 3:2 (bet + 1.5x bet = 2.5x bet total)
        const winnings = betAmount * 1.5;
        return await this.creditWinnings(winnings);
      } else if (outcome === 'win') {
        // Regular win pays 1:1 (bet + bet = 2x bet total, so credit bet amount as winnings)
        const winnings = betAmount + (splitBetAmount || 0);
        return await this.creditWinnings(winnings);
      } else {
        // Lose: record loss (balance already deducted with bet)
        return await this.recordLoss(betAmount + (splitBetAmount || 0));
      }
    } catch (error) {
      console.error('Failed to handle Blackjack result:', error);
      throw error;
    }
  }

  /**
   * Handle Roulette game result
   */
  static async handleRouletteResult(
    totalBetAmount: number,
    totalPayout: number
  ): Promise<number> {
    try {
      if (totalPayout > 0) {
        // Won: credit the winnings (payout - stake)
        const netWinnings = totalPayout - totalBetAmount;
        return await this.creditWinnings(netWinnings);
      } else {
        // Lost: record loss (balance already deducted with bet)
        return await this.recordLoss(totalBetAmount);
      }
    } catch (error) {
      console.error('Failed to handle Roulette result:', error);
      throw error;
    }
  }
}

